import { useQuery } from '@tanstack/react-query';
import { Cake, Newspaper, Settings } from 'lucide-react';
import { DateTime } from 'luxon';
import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';

import { DashboardCustomizerDialog } from '@/components/reporting/dashboard-customizer';
import { DashboardLayoutSwitcher } from '@/components/reporting/dashboard-layout-switcher';
import { dashboardDataQueryKey } from '@/components/reporting/dashboard-layout-content';
import {
  formatDashboardChange,
  METRIC_TIP_IDS,
  DASHBOARD_WIDGET_ROUTES,
  type DashboardMetric,
  type DashboardResponse,
  type BirthdayPerson,
  type DashboardContentPreview,
} from '@/components/reporting/dashboard-content';
import { DashboardCalendarTile } from '@/components/reporting/dashboard-calendar-tile';
import { DASHBOARD_METRIC_ICONS } from '@/components/reporting/dashboard-metric-icons';
import {
  DashboardContentPreviewList,
  DashboardFollowUpPreviewList,
  DashboardJoinRequestPreviewList,
  DashboardPersonPreviewList,
} from '@/components/reporting/dashboard-preview-rows';
import { ChartCard } from '@/components/shared/chart-card';
import {
  CollapsibleInsights,
  DASHBOARD_INSIGHTS_OPEN_STORAGE_KEY,
} from '@/components/shared/collapsible-insights';
import {
  DateRangeFilter,
  DASHBOARD_DATE_RANGE_PRESETS,
  resolvePreset,
  type DateRange,
} from '@/components/shared/date-range-filter';
import { MemberNameLink } from '@/components/shared/member-name-link';
import { PageHeader } from '@/components/shared/page-header';
import { EmptyState } from '@/components/shared/states';
import { GivingTrendChart } from '@/components/giving/giving-trend-chart';
import { SimpleBarChart } from '@/components/shared/simple-bar-chart';
import { SimpleDonutChart } from '@/components/shared/simple-donut-chart';
import { StatCard } from '@/components/shared/stat-card';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from '@/components/ui/toast';
import { useDashboardLayouts } from '@/hooks/use-dashboard-layouts';
import { api } from '@/lib/api';
import { useChurchStore } from '@/stores/church-store';
import { usePreferences } from '@/stores/preferences-store';
import { cn } from '@/utils/cn';
import { formatMoney, formatNumber } from '@/utils/format';

const hasWidget = (widgets: Set<string>, key: string) => widgets.has(key);

/** Two-column grid with consistent gaps — stretch row siblings to equal height. */
const DASHBOARD_PANEL_GRID_CLASS = 'grid items-stretch gap-4 md:grid-cols-2';

const DashboardPanelItem = ({
  children,
  fullWidth = false,
}: {
  children: ReactNode;
  fullWidth?: boolean;
}) => (
  <div className={cn('h-full min-w-0 w-full', fullWidth && 'md:col-span-2')}>{children}</div>
);

const DASHBOARD_GIVING_PLOT_HEIGHT = 200;
const DASHBOARD_GIVING_CARD_CLASS = 'flex h-[18.25rem] flex-col overflow-hidden';
const DASHBOARD_GIVING_BY_FUND_PREVIEW_LIMIT = 3;
/** Aligns the by-fund header with the trend chart's fund filter row. */
const DASHBOARD_GIVING_HEADER_ACTIONS_SPACER = (
  <span className="block h-9 w-48 shrink-0" aria-hidden />
);

const DASHBOARD_MEMBER_INSIGHT_CARD_CLASS = 'flex h-[17rem] flex-col overflow-hidden';
const DASHBOARD_MEMBER_INSIGHT_BODY_CLASS =
  'grid h-full min-h-0 flex-1 grid-cols-2 items-stretch gap-4 overflow-hidden p-5 max-sm:grid-cols-1';
const DASHBOARD_MEMBER_INSIGHT_PLOT_HEIGHT = 176;
const DASHBOARD_MEMBER_GROUP_PREVIEW_LIMIT = 3;

/** Keep the dashboard card short; the full month lives on /people/birthdays. */
const BIRTHDAY_PREVIEW_LIMIT = 5;
const RECENT_CONTENT_PREVIEW_LIMIT = 2;
const METRIC_PREVIEW_LIMIT = 3;

const personName = (person: BirthdayPerson) =>
  [person.firstName, person.lastName].filter(Boolean).join(' ');

const metricPreviewFooter = ({
  metricKey,
  previews,
  locale,
}: {
  metricKey: string;
  previews: DashboardResponse['previews'] | undefined;
  locale: string;
}) => {
  if (!previews) return null;

  if (metricKey === 'new_members_in_period' && previews.newMembers.length > 0) {
    return (
      <MetricPreviewBlock
        items={previews.newMembers}
        viewAllTo={DASHBOARD_WIDGET_ROUTES.new_members_in_period}
        render={(people) => (
          <DashboardPersonPreviewList people={people} locale={locale} showDate showPhoto />
        )}
      />
    );
  }
  if (metricKey === 'first_timers_in_period' && previews.firstTimers.length > 0) {
    return (
      <MetricPreviewBlock
        items={previews.firstTimers}
        viewAllTo={DASHBOARD_WIDGET_ROUTES.first_timers_in_period}
        render={(people) => (
          <DashboardPersonPreviewList people={people} locale={locale} showDate showPhoto />
        )}
      />
    );
  }
  if (metricKey === 'open_follow_ups' && previews.openFollowUps.length > 0) {
    return (
      <MetricPreviewBlock
        items={previews.openFollowUps}
        viewAllTo={DASHBOARD_WIDGET_ROUTES.open_follow_ups}
        render={(items) => <DashboardFollowUpPreviewList items={items} locale={locale} />}
      />
    );
  }
  if (metricKey === 'pending_member_requests' && previews.pendingMemberRequests.length > 0) {
    return (
      <MetricPreviewBlock
        items={previews.pendingMemberRequests}
        viewAllTo={DASHBOARD_WIDGET_ROUTES.pending_member_requests}
        render={(items) => <DashboardJoinRequestPreviewList items={items} />}
      />
    );
  }
  return null;
};

const MetricPreviewBlock = <T,>({
  items,
  viewAllTo,
  render,
}: {
  items: T[];
  viewAllTo?: string;
  render: (items: T[]) => ReactNode;
}) => {
  const { t } = useTranslation();
  const preview = items.slice(0, METRIC_PREVIEW_LIMIT);
  return (
    <div>
      {render(preview)}
      {viewAllTo ? (
        <Link
          to={viewAllTo}
          className="mt-2 inline-flex rounded-sm text-caption font-medium text-brand hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand"
        >
          {t('action.viewAll')}
        </Link>
      ) : null}
    </div>
  );
};

/** Anniversary day only — birth year is not the point of a birthday list. */
const formatBirthdayDay = (iso: string, locale: string): string => {
  const parsed = DateTime.fromISO(iso, { zone: 'utc' }).setLocale(locale);
  if (!parsed.isValid) return '—';
  return parsed.toFormat('MMM d');
};

export const ChurchHomePage = () => {
  const { t } = useTranslation('reports');
  const { t: tChurches } = useTranslation('churches');
  const { t: tCommon } = useTranslation();
  const church = useChurchStore((state) => state.church);
  const membership = useChurchStore((state) => state.membership);
  const isCongregant = membership?.systemRole === 'congregant';
  const subscription = useChurchStore((state) => state.subscription);
  // Subscribe to the module list — selecting `isModuleEnabled` alone never re-renders
  // when modules finish loading, which leaves the dashboard query disabled forever.
  const reportingEnabled = useChurchStore((state) =>
    state.enabledModules.some((module) => module.code === 'reporting'),
  );
  const timezone = usePreferences((state) => state.timezone);
  const locale = usePreferences((state) => state.locale);

  const [dateRange, setDateRange] = useState<DateRange>(() => ({
    preset: 'thisMonth',
    ...resolvePreset('thisMonth', timezone),
  }));
  const [activeLayoutIndex, setActiveLayoutIndex] = useState(0);
  const [activeWidgetKeys, setActiveWidgetKeys] = useState<string[]>([]);
  const [customizeOpen, setCustomizeOpen] = useState(false);
  const {
    layouts,
    activeLayout,
    activeLayoutIndex: serverActiveLayoutIndex,
    activeLayoutId,
    activateMutation,
    query: layoutsQuery,
  } = useDashboardLayouts();

  useEffect(() => {
    if (!activeLayout) return;
    setActiveLayoutIndex(serverActiveLayoutIndex);
    setActiveWidgetKeys(activeLayout.widgets);
  }, [activeLayout, serverActiveLayoutIndex]);

  const handleWidgetsChange = useCallback((widgets: string[]) => {
    setActiveWidgetKeys(widgets);
  }, []);

  const handleActivateLayout = useCallback(
    (layoutId: string) => {
      if (layoutId === activeLayoutId) return;
      activateMutation.mutate(layoutId, {
        onError: () => {
          toast.error(t('home.layouts.switchFailed'));
        },
      });
    },
    [activateMutation, activeLayoutId, t],
  );

  const isLayoutSwitching = activateMutation.isPending;
  const switchingLayout =
    isLayoutSwitching && activateMutation.variables
      ? layouts.find((layout) => layout.id === activateMutation.variables)
      : null;
  const visibleWidgets = useMemo(() => {
    if (switchingLayout) return new Set(switchingLayout.widgets);
    return new Set(activeWidgetKeys);
  }, [switchingLayout, activeWidgetKeys]);

  const dateFrom = dateRange.from;
  const dateTo = dateRange.to;

  const dashboard = useQuery({
    queryKey: dashboardDataQueryKey(church?.id, dateFrom, dateTo),
    queryFn: () =>
      api.get<DashboardResponse>('/dashboard', {
        query: {
          dateFrom: dateFrom ?? undefined,
          dateTo: dateTo ?? undefined,
        },
      }),
    enabled: Boolean(church?.id) && reportingEnabled && !isCongregant,
  });

  const overviewLoading =
    reportingEnabled &&
    (dashboard.isPending || layoutsQuery.isPending || isLayoutSwitching);

  const rangeLabels = useMemo(
    () => ({
      today: tCommon('date.today'),
      yesterday: tCommon('date.yesterday'),
      thisWeek: tCommon('date.thisWeek'),
      lastWeek: tCommon('date.lastWeek'),
      thisMonth: tCommon('date.thisMonth'),
      lastMonth: tCommon('date.lastMonth'),
      thisQuarter: tCommon('date.thisQuarter'),
      thisYear: tCommon('date.thisYear'),
      lastYear: tCommon('date.lastYear'),
      allTime: tCommon('date.allTime'),
      custom: tCommon('date.custom'),
      from: t('run.dateFrom'),
      to: t('run.dateTo'),
    }),
    [t, tCommon],
  );

  const categoryLabel = (key: string) => {
    const translated = t(`value.${key}`, { defaultValue: '' });
    return translated || key;
  };

  if (!church) return null;

  if (isCongregant) {
    return (
      <div className="space-y-6">
        <PageHeader title={tChurches('congregantHome.title')} />
        <EmptyState
          title={tChurches('congregantHome.title')}
          body={tChurches('congregantHome.body')}
          action={
            <Button asChild>
              <Link to="/churches/new">{tChurches('congregantHome.createChurch')}</Link>
            </Button>
          }
        />
      </div>
    );
  }

  if (!subscription) return null;

  const charts = dashboard.data?.charts;
  const birthdays = dashboard.data?.birthdays;
  const previews = dashboard.data?.previews;
  const calendar = dashboard.data?.calendar;
  const content = dashboard.data?.content;

  return (
    <div className="space-y-6">
      <PageHeader
        title={t('home.title')}
        description={t('home.description')}
        headerActions={
          reportingEnabled ? (
            <>
              <Button
                type="button"
                variant="secondary"
                size="icon"
                aria-label={t('home.layouts.open')}
                onClick={() => setCustomizeOpen(true)}
              >
                <Settings className="size-4" aria-hidden />
              </Button>
              <DateRangeFilter
                value={dateRange}
                onChange={setDateRange}
                labels={rangeLabels}
                presets={DASHBOARD_DATE_RANGE_PRESETS}
              />
              <Button asChild variant="secondary">
                <Link to="/reports">{t('home.viewReports')}</Link>
              </Button>
            </>
          ) : undefined
        }
      />

      {reportingEnabled ? (
        <>
          <DashboardCustomizerDialog
            open={customizeOpen}
            onOpenChange={setCustomizeOpen}
            activeIndex={activeLayoutIndex}
            onActiveIndexChange={setActiveLayoutIndex}
            onWidgetsChange={handleWidgetsChange}
            dateFrom={dateFrom ?? undefined}
            dateTo={dateTo ?? undefined}
            baseCurrency={church.baseCurrency}
            locale={locale}
          />

          <CollapsibleInsights
              storageKey={DASHBOARD_INSIGHTS_OPEN_STORAGE_KEY}
              headerTrailing={
                <DashboardLayoutSwitcher
                  layouts={layouts}
                  activeLayoutId={activeLayoutId}
                  loading={isLayoutSwitching}
                  disabled={isLayoutSwitching}
                  onActivate={handleActivateLayout}
                />
              }
              summary={
                overviewLoading
                  ? undefined
                  : (dashboard.data?.metrics ?? [])
                      .filter((metric) => hasWidget(visibleWidgets, metric.key))
                      .slice(0, 4)
                      .map((metric) => ({
                        label: t(metric.nameKey),
                        value: metric.money
                          ? formatMoney(metric.money.amountMinor, metric.money.currency)
                          : metric.value === null || metric.value === undefined
                            ? '—'
                            : formatNumber(metric.value),
                      }))
              }
            >
          {overviewLoading ? (
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5">
                {[0, 1, 2, 3, 4, 5, 6, 7].map((key) => (
                  <StatCard key={key} label={tCommon('state.loading')} loading />
                ))}
              </div>
          ) : dashboard.data?.metrics.length ? (
            (() => {
              const visibleMetrics = dashboard.data.metrics.filter((metric) =>
                hasWidget(visibleWidgets, metric.key),
              );
              const renderMetric = (metric: DashboardMetric) => {
                const footer = metricPreviewFooter({
                  metricKey: metric.key,
                  previews,
                  locale,
                });
                return (
                  <StatCard
                    key={metric.key}
                    to={footer ? undefined : DASHBOARD_WIDGET_ROUTES[metric.key]}
                    label={t(metric.nameKey)}
                    value={
                      metric.value === null || metric.value === undefined
                        ? undefined
                        : formatNumber(metric.value)
                    }
                    money={
                      metric.money
                        ? { amount: metric.money.amountMinor, currency: metric.money.currency }
                        : undefined
                    }
                    icon={DASHBOARD_METRIC_ICONS[metric.key]}
                    comparison={
                      metric.comparison
                        ? {
                            change: metric.comparison.change,
                            label: t('dashboard.vsPreviousPeriod'),
                            formatted: formatDashboardChange(metric.comparison.change),
                          }
                        : undefined
                    }
                    tipId={METRIC_TIP_IDS[metric.key]}
                    footer={footer}
                  />
                );
              };

              return (
                <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5">
                  {visibleMetrics.map(renderMetric)}
                </div>
              );
            })()
          ) : null}

          {visibleWidgets.size === 0 && !overviewLoading ? (
            <p className="text-body-sm text-fg-muted">{t('home.layouts.emptySelection')}</p>
          ) : null}

          <div className={DASHBOARD_PANEL_GRID_CLASS}>
            {hasWidget(visibleWidgets, 'chart_giving_trend') ? (
              <DashboardPanelItem>
            <GivingTrendChart
              title={t('dashboard.charts.givingTrend')}
              emptyTitle={t('dashboard.charts.givingTrendEmpty')}
              to={DASHBOARD_WIDGET_ROUTES.chart_giving_trend}
              dateFrom={dateFrom}
              dateTo={dateTo}
              baseCurrency={church.baseCurrency}
              fallbackPoints={charts?.givingTrend}
              fallbackLoading={overviewLoading}
              height={DASHBOARD_GIVING_PLOT_HEIGHT}
              className={DASHBOARD_GIVING_CARD_CLASS}
              plotFixed
            />
              </DashboardPanelItem>
            ) : null}

            {hasWidget(visibleWidgets, 'chart_giving_by_fund') ? (
              <DashboardPanelItem>
            {(() => {
              const fundPoints = charts?.givingByFund ?? [];
              const previewFunds = fundPoints.slice(0, DASHBOARD_GIVING_BY_FUND_PREVIEW_LIMIT);
              const hasMoreFunds = fundPoints.length > previewFunds.length;

              return (
            <ChartCard
              title={t('dashboard.charts.givingByFund')}
              to={DASHBOARD_WIDGET_ROUTES.chart_giving_by_fund}
              loading={overviewLoading}
              hasData={fundPoints.length > 0}
              empty={{ title: t('dashboard.charts.givingByFundEmpty') }}
              height={DASHBOARD_GIVING_PLOT_HEIGHT}
              className={DASHBOARD_GIVING_CARD_CLASS}
              plotFixed
              actions={DASHBOARD_GIVING_HEADER_ACTIONS_SPACER}
            >
              <div className="flex h-full min-h-0 flex-col">
                <div className="min-h-0 flex-1 overflow-hidden">
                  <SimpleBarChart
                    orientation="horizontal"
                    data={previewFunds.map((point) => ({
                      label: point.label,
                      value: point.value,
                      currency: point.currency ?? church.baseCurrency,
                    }))}
                    formatValue={(value, item) =>
                      formatMoney(value, item?.currency ?? church.baseCurrency, { showSymbol: true })
                    }
                    height={
                      hasMoreFunds
                        ? DASHBOARD_GIVING_PLOT_HEIGHT - 28
                        : DASHBOARD_GIVING_PLOT_HEIGHT
                    }
                  />
                </div>
                {hasMoreFunds ? (
                  <Link
                    to={DASHBOARD_WIDGET_ROUTES.chart_giving_by_fund!}
                    className="mt-2 shrink-0 text-caption font-medium text-brand hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand"
                  >
                    {tCommon('action.viewAll')}
                  </Link>
                ) : null}
              </div>
            </ChartCard>
              );
            })()}
              </DashboardPanelItem>
            ) : null}

            {hasWidget(visibleWidgets, 'chart_members_by_gender') ||
            hasWidget(visibleWidgets, 'chart_members_by_type') ? (
              <DashboardPanelItem>
              <Card className={DASHBOARD_MEMBER_INSIGHT_CARD_CLASS}>
                <CardContent
                  className={cn(
                    DASHBOARD_MEMBER_INSIGHT_BODY_CLASS,
                    hasWidget(visibleWidgets, 'chart_members_by_gender') &&
                      hasWidget(visibleWidgets, 'chart_members_by_type')
                      ? 'grid-cols-2'
                      : 'grid-cols-1',
                  )}
                >
                {hasWidget(visibleWidgets, 'chart_members_by_gender') ? (
            <MemberBreakdownDonutSection
              compact={
                hasWidget(visibleWidgets, 'chart_members_by_gender') &&
                hasWidget(visibleWidgets, 'chart_members_by_type')
              }
              title={t('dashboard.charts.membersByGender')}
              to={DASHBOARD_WIDGET_ROUTES.chart_members_by_gender!}
              loading={overviewLoading}
              hasData={(charts?.membersByGender.length ?? 0) > 0}
              emptyTitle={t('dashboard.charts.breakdownEmpty')}
              data={(charts?.membersByGender ?? []).map((point) => ({
                label: categoryLabel(point.key),
                value: point.value,
              }))}
              centerValue={formatNumber(
                (charts?.membersByGender ?? []).reduce((sum, point) => sum + point.value, 0),
              )}
              plotHeight={DASHBOARD_MEMBER_INSIGHT_PLOT_HEIGHT}
            />
                ) : null}
                {hasWidget(visibleWidgets, 'chart_members_by_type') ? (
            <MemberBreakdownDonutSection
              compact={
                hasWidget(visibleWidgets, 'chart_members_by_gender') &&
                hasWidget(visibleWidgets, 'chart_members_by_type')
              }
              title={t('dashboard.charts.membersByType')}
              to={DASHBOARD_WIDGET_ROUTES.chart_members_by_type!}
              loading={overviewLoading}
              hasData={(charts?.membersByType.length ?? 0) > 0}
              emptyTitle={t('dashboard.charts.breakdownEmpty')}
              data={(charts?.membersByType ?? []).map((point) => ({
                label: categoryLabel(point.key),
                value: point.value,
              }))}
              centerValue={formatNumber(
                (charts?.membersByType ?? []).reduce((sum, point) => sum + point.value, 0),
              )}
              plotHeight={DASHBOARD_MEMBER_INSIGHT_PLOT_HEIGHT}
            />
                ) : null}
                </CardContent>
              </Card>
              </DashboardPanelItem>
            ) : null}

            {hasWidget(visibleWidgets, 'chart_members_by_group') &&
            ((charts?.membersByAutoFilledGroup.length ?? 0) > 0 ||
              (charts?.membersByManualGroup.length ?? 0) > 0 ||
              overviewLoading) ? (
              <DashboardPanelItem>
              <Card className={DASHBOARD_MEMBER_INSIGHT_CARD_CLASS}>
                <CardContent className={DASHBOARD_MEMBER_INSIGHT_BODY_CLASS}>
                  <MemberGroupBarSection
                    title={t('dashboard.charts.membersByManualGroup')}
                    to={DASHBOARD_WIDGET_ROUTES.chart_members_by_group!}
                    loading={overviewLoading}
                    hasData={(charts?.membersByManualGroup.length ?? 0) > 0}
                    emptyTitle={t('dashboard.charts.breakdownEmpty')}
                    data={(charts?.membersByManualGroup ?? []).map((point) => ({
                      label: point.label,
                      value: point.value,
                    }))}
                    previewLimit={DASHBOARD_MEMBER_GROUP_PREVIEW_LIMIT}
                    plotHeight={DASHBOARD_MEMBER_INSIGHT_PLOT_HEIGHT}
                    viewAllLabel={tCommon('action.viewAll')}
                  />
                  <MemberGroupBarSection
                    title={t('dashboard.charts.membersByAutoFilledGroup')}
                    to={DASHBOARD_WIDGET_ROUTES.chart_members_by_group!}
                    loading={overviewLoading}
                    hasData={(charts?.membersByAutoFilledGroup.length ?? 0) > 0}
                    emptyTitle={t('dashboard.charts.breakdownEmpty')}
                    data={(charts?.membersByAutoFilledGroup ?? []).map((point) => ({
                      label: point.label,
                      value: point.value,
                    }))}
                    previewLimit={DASHBOARD_MEMBER_GROUP_PREVIEW_LIMIT}
                    plotHeight={DASHBOARD_MEMBER_INSIGHT_PLOT_HEIGHT}
                    viewAllLabel={tCommon('action.viewAll')}
                  />
                </CardContent>
              </Card>
              </DashboardPanelItem>
            ) : null}

            {(() => {
              const hasCalendar = hasWidget(visibleWidgets, 'calendar');
              const hasBirthdays = hasWidget(visibleWidgets, 'birthdays');
              const hasRecentSermons = hasWidget(visibleWidgets, 'recent_sermons');
              const hasRecentAnnouncements = hasWidget(visibleWidgets, 'recent_announcements');
              const hasRecentDevotionals = hasWidget(visibleWidgets, 'recent_devotionals');
              const hasRecentsColumn =
                hasRecentSermons || hasRecentAnnouncements || hasRecentDevotionals;
              const lifestyleColumnCount =
                Number(hasCalendar) + Number(hasBirthdays) + Number(hasRecentsColumn);

              if (lifestyleColumnCount === 0) return null;

              const lifestyleGridClass =
                lifestyleColumnCount >= 3
                  ? 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3'
                  : lifestyleColumnCount === 2
                    ? 'grid-cols-1 md:grid-cols-2'
                    : 'grid-cols-1';

              return (
                <DashboardPanelItem fullWidth>
                  <div className={cn('grid items-stretch gap-4', lifestyleGridClass)}>
                    {hasCalendar ? (
                      <DashboardCalendarTile
                        className="h-full"
                        month={calendar?.month ?? dateFrom?.slice(0, 7) ?? ''}
                        days={calendar?.days ?? []}
                        loading={overviewLoading}
                      />
                    ) : null}
                    {hasBirthdays ? (
                      <Card className="flex h-full flex-col">
                        <CardHeader className="flex-row items-center justify-between gap-3">
                          <CardTitle className="flex items-center gap-2">
                            <Cake className="size-4 text-fg-tertiary" aria-hidden />
                            {t('dashboard.birthdays.title')}
                          </CardTitle>
                          <Button variant="ghost" size="sm" asChild>
                            <Link to={DASHBOARD_WIDGET_ROUTES.birthdays!}>
                              {tCommon('action.viewAll')}
                            </Link>
                          </Button>
                        </CardHeader>
                        <CardContent className="flex flex-1 flex-col gap-6">
                          <BirthdayList
                            title={t('dashboard.birthdays.today')}
                            empty={t('dashboard.birthdays.todayEmpty')}
                            people={birthdays?.today ?? []}
                            loading={overviewLoading}
                            locale={locale}
                            viewAllTo={DASHBOARD_WIDGET_ROUTES.birthdays}
                            viewAllLabel={tCommon('action.viewAll')}
                          />
                          <BirthdayList
                            title={t('dashboard.birthdays.thisMonth')}
                            empty={t('dashboard.birthdays.monthEmpty')}
                            people={birthdays?.thisMonth ?? []}
                            loading={overviewLoading}
                            locale={locale}
                            showDate
                            viewAllTo={DASHBOARD_WIDGET_ROUTES.birthdays}
                            viewAllLabel={tCommon('action.viewAll')}
                          />
                        </CardContent>
                      </Card>
                    ) : null}
                    {hasRecentsColumn ? (
                      <Card className="flex h-full flex-col">
                        <CardHeader className="flex-row items-center justify-between gap-3">
                          <CardTitle className="flex items-center gap-2">
                            <Newspaper className="size-4 text-fg-tertiary" aria-hidden />
                            {t('dashboard.content.recents')}
                          </CardTitle>
                        </CardHeader>
                        <CardContent className="flex flex-1 flex-col gap-5">
                          {hasRecentSermons ? (
                            <RecentContentList
                              title={t('dashboard.content.sermons')}
                              empty={t('dashboard.content.sermonsEmpty')}
                              items={content?.sermons ?? []}
                              loading={overviewLoading}
                              locale={locale}
                              to={DASHBOARD_WIDGET_ROUTES.recent_sermons!}
                              viewAllLabel={tCommon('action.viewAll')}
                            />
                          ) : null}
                          {hasRecentAnnouncements ? (
                            <RecentContentList
                              title={t('dashboard.content.announcements')}
                              empty={t('dashboard.content.announcementsEmpty')}
                              items={content?.announcements ?? []}
                              loading={overviewLoading}
                              locale={locale}
                              to={DASHBOARD_WIDGET_ROUTES.recent_announcements!}
                              viewAllLabel={tCommon('action.viewAll')}
                            />
                          ) : null}
                          {hasRecentDevotionals ? (
                            <RecentContentList
                              title={t('dashboard.content.devotionals')}
                              empty={t('dashboard.content.devotionalsEmpty')}
                              items={content?.devotionals ?? []}
                              loading={overviewLoading}
                              locale={locale}
                              to={DASHBOARD_WIDGET_ROUTES.recent_devotionals!}
                              viewAllLabel={tCommon('action.viewAll')}
                            />
                          ) : null}
                        </CardContent>
                      </Card>
                    ) : null}
                  </div>
                </DashboardPanelItem>
              );
            })()}
          </div>
          </CollapsibleInsights>
        </>
      ) : (
        <Card>
          <CardContent className="flex flex-col gap-2 py-6 text-body-sm text-fg-muted">
            <p className="text-card-title text-fg">{church.name}</p>
            <p>{tChurches('home.plan', { plan: subscription.plan })}</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

const MemberGroupBarSection = ({
  title,
  to,
  loading,
  hasData,
  emptyTitle,
  data,
  previewLimit = 3,
  plotHeight = 176,
  viewAllLabel,
}: {
  title: string;
  to: string;
  loading: boolean;
  hasData: boolean;
  emptyTitle: string;
  data: Array<{ label: string; value: number }>;
  previewLimit?: number;
  plotHeight?: number;
  viewAllLabel?: string;
}) => {
  const preview = data.slice(0, previewLimit);
  const hasMore = data.length > preview.length;

  return (
  <section className="flex min-h-0 min-w-0 flex-col">
    <Link
      to={to}
      className="text-card-title font-semibold text-fg rounded-sm hover:text-brand focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand"
    >
      {title}
    </Link>
    <div className="mt-3 flex min-h-0 flex-1 flex-col overflow-hidden">
      {loading ? (
        <p className="text-body-sm text-fg-muted">…</p>
      ) : !hasData ? (
        <p className="text-body-sm text-fg-muted">{emptyTitle}</p>
      ) : (
        <>
          <div className="min-h-0 flex-1 overflow-hidden">
            <SimpleBarChart
              orientation="horizontal"
              data={preview}
              formatValue={(value) => formatNumber(value)}
              height={hasMore ? plotHeight - 28 : plotHeight}
            />
          </div>
          {hasMore && viewAllLabel ? (
            <Link
              to={to}
              className="mt-2 shrink-0 text-caption font-medium text-brand hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand"
            >
              {viewAllLabel}
            </Link>
          ) : null}
        </>
      )}
    </div>
  </section>
  );
};

const MemberBreakdownDonutSection = ({
  title,
  to,
  loading,
  hasData,
  emptyTitle,
  data,
  centerValue,
  compact = false,
  plotHeight = 176,
}: {
  title: string;
  to: string;
  loading: boolean;
  hasData: boolean;
  emptyTitle: string;
  data: Array<{ label: string; value: number }>;
  centerValue: string;
  compact?: boolean;
  plotHeight?: number;
}) => (
  <section className="flex min-h-0 min-w-0 flex-col">
    <Link
      to={to}
      className="text-card-title font-semibold text-fg rounded-sm hover:text-brand focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand"
    >
      {title}
    </Link>
    <div
      className="mt-3 flex w-full items-center justify-center overflow-hidden"
      style={{ height: plotHeight, maxHeight: plotHeight }}
    >
      {loading ? (
        <p className="text-body-sm text-fg-muted">…</p>
      ) : !hasData ? (
        <p className="text-body-sm text-fg-muted">{emptyTitle}</p>
      ) : (
        <SimpleDonutChart
          className={compact ? 'flex-col items-center justify-center gap-2' : undefined}
          data={data}
          formatValue={(value) => formatNumber(value)}
          centerValue={centerValue}
          size={compact ? 120 : 148}
        />
      )}
    </div>
  </section>
);

const RecentContentList = ({
  title,
  empty,
  items,
  loading,
  locale,
  to,
  limit = RECENT_CONTENT_PREVIEW_LIMIT,
  viewAllLabel,
}: {
  title: string;
  empty: string;
  items: DashboardContentPreview[];
  loading: boolean;
  locale: string;
  to: string;
  limit?: number;
  viewAllLabel?: string;
}) => {
  const preview = items.slice(0, limit);
  const hasMore = items.length > preview.length;

  return (
    <div>
      <Link
        to={to}
        className="text-body-sm font-medium text-fg-secondary rounded-sm hover:text-brand focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand"
      >
        {title}
      </Link>
      {loading ? (
        <p className="mt-2 text-body-sm text-fg-muted">…</p>
      ) : items.length === 0 ? (
        <p className="mt-2 text-body-sm text-fg-muted">{empty}</p>
      ) : (
        <>
          <div className="mt-2">
            <DashboardContentPreviewList items={preview} locale={locale} to={to} />
          </div>
          {hasMore && viewAllLabel ? (
            <Link
              to={to}
              className="mt-2 inline-flex rounded-sm text-body-sm font-medium text-brand hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand"
            >
              {viewAllLabel}
            </Link>
          ) : null}
        </>
      )}
    </div>
  );
};

const BirthdayList = ({
  title,
  empty,
  people,
  loading,
  locale,
  showDate = false,
  limit = BIRTHDAY_PREVIEW_LIMIT,
  viewAllTo,
  viewAllLabel,
}: {
  title: string;
  empty: string;
  people: BirthdayPerson[];
  loading: boolean;
  locale: string;
  showDate?: boolean;
  limit?: number;
  viewAllTo?: string;
  viewAllLabel?: string;
}) => {
  const preview = people.slice(0, limit);
  const hasMore = people.length > limit;

  return (
    <div>
      <p className="text-body-sm font-medium text-fg-secondary">{title}</p>
      {loading ? (
        <p className="mt-2 text-body-sm text-fg-muted">…</p>
      ) : people.length === 0 ? (
        <p className="mt-2 text-body-sm text-fg-muted">{empty}</p>
      ) : (
        <>
          <ul className="mt-2 space-y-1.5">
            {preview.map((person) => (
              <li key={person.id}>
                <div className="flex items-center justify-between gap-3 rounded-md px-1 py-1 text-body-sm">
                  <MemberNameLink
                    memberId={person.id}
                    name={personName(person)}
                    className="font-medium"
                  />
                  {showDate ? (
                    <span className="shrink-0 text-caption text-fg-tertiary tabular-nums">
                      {formatBirthdayDay(person.dateOfBirth, locale)}
                    </span>
                  ) : null}
                </div>
              </li>
            ))}
          </ul>
          {hasMore && viewAllTo && viewAllLabel ? (
            <Link
              to={viewAllTo}
              className="mt-2 inline-flex rounded-sm text-body-sm font-medium text-brand hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand"
            >
              {viewAllLabel}
            </Link>
          ) : null}
        </>
      )}
    </div>
  );
};
